// Pantalla minimalista para generar audio Speech-to-Speech. El plugin la abre
// vía openExternal: /generar-audio?voice_id=...&project_id=... El usuario graba
// o sube audio (DialogueAudioInput) y genera con la voz elegida. El resultado
// queda como asset del proyecto; el plugin lo importa con su botón ↻.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import DialogueAudioInput, {
  DialogueAudioValue,
} from "../components/DialogueAudioInput";
import { generateS2SAudio } from "../lib/s2sAudio";

type SubmitState = "idle" | "submitting" | "success" | "error";

const GenerarAudio: React.FC = () => {
  const [searchParams] = useSearchParams();
  const voiceId = searchParams.get("voice_id") || "";
  const projectId = searchParams.get("project_id") || "";

  const [authChecked, setAuthChecked] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);

  const [audio, setAudio] = useState<DialogueAudioValue | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      if (!session) {
        const next = "/generar-audio" + window.location.search;
        window.location.replace("/auth?next=" + encodeURIComponent(next));
        return;
      }
      if (!voiceId || !projectId) {
        setFatalError("Faltan datos (voz o proyecto). Reabrí desde Atlas.");
        setAuthChecked(true);
        return;
      }
      const uid = session.user.id;
      const { data, error } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", uid)
        .single();
      if (cancelled) return;
      if (error || !data?.id_company) {
        setFatalError("No pudimos resolver tu empresa. Reintentá desde Atlas.");
      } else {
        setUserId(uid);
        setCompanyId(data.id_company as string);
      }
      setAuthChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [voiceId, projectId]);

  const canSubmit =
    !!audio && submitState !== "submitting" && !!userId && !!companyId;

  const handleSubmit = async () => {
    if (!audio || !userId || !companyId) return;
    setSubmitState("submitting");
    setErrorMessage(null);
    try {
      await generateS2SAudio({
        projectId,
        companyId,
        userId,
        voiceId,
        audioUrl: audio.url,
        audioDurationSec: audio.durationSec,
      });
      setSubmitState("success");
    } catch (e) {
      setErrorMessage(
        e instanceof Error
          ? e.message
          : "Hubo un problema técnico. Intentá de nuevo.",
      );
      setSubmitState("error");
    }
  };

  if (!authChecked && !fatalError) {
    return (
      <div className="min-h-[100dvh] bg-surface-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-surface-bg flex flex-col items-center justify-center px-4 py-8 font-sans">
      <img
        src="/images/estudio-atlas-logo-solo.svg"
        alt="Atlas"
        className="w-12 h-12 mb-6"
      />
      <div className="w-full max-w-md bg-surface-card rounded-lg shadow-md p-6">
        {fatalError ? (
          <div className="text-center py-4">
            <AlertCircle className="w-8 h-8 text-error mx-auto mb-2" />
            <p className="text-[14px] text-brand-black/80">{fatalError}</p>
          </div>
        ) : submitState === "success" ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-12 h-12 text-brand-primary mx-auto mb-3" />
            <h2 className="text-[18px] font-semibold text-brand-black">
              ¡Audio generado!
            </h2>
            <p className="text-[14px] text-brand-black/70 mt-2 leading-relaxed">
              Volvé a Premiere (Alt+Tab) y tocá ↻ "Traer audio generado" para
              importarlo al bin "Atlas — Generados".
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-[20px] font-semibold text-brand-black mb-1">
              Grabá tu voz
            </h1>
            <p className="text-[13px] text-brand-black/60 mb-5">
              Grabá o subí un audio. La voz elegida lo va a "imitar",
              conservando tus pausas y entonación.
            </p>

            <DialogueAudioInput
              value={audio}
              onChange={setAudio}
              projectId={projectId}
              companyId={companyId}
              userId={userId}
              hideUpload
            />

            {errorMessage && (
              <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 my-4">
                <p className="text-[13px] text-red-700 flex items-start gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{errorMessage}</span>
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-[14.5px] font-semibold transition-colors mt-4 ${
                canSubmit
                  ? "bg-brand-primary text-white hover:bg-brand-primary-hover"
                  : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
              }`}
            >
              {submitState === "submitting" && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {submitState === "submitting" ? "Generando…" : "Generar"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GenerarAudio;
